import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Permission, hasPermission, Role } from '@shared/permissions';

// Permission cache for the current session
const permissionCache = new Map<string, boolean>();

// Cache expiration time (4 hours in milliseconds)
const CACHE_EXPIRY = 4 * 60 * 60 * 1000;

// Cache with expiration timestamps
const permissionCacheTimestamps = new Map<string, number>();

// Function to clear the permission cache
export function clearPermissionCache() {
  permissionCache.clear();
  permissionCacheTimestamps.clear();
}

type PermissionUser = NonNullable<ReturnType<typeof useAuthStore.getState>['user']>;

function cachedHasPermission(user: PermissionUser, requiredPermission: Permission): boolean {
  const cacheKey = `${user.id}|${user.role}|${user.active_team?.id || 'no-team'}|${requiredPermission}`;
  const now = Date.now();
  
  // Check if cache entry exists and is still valid
  if (permissionCache.has(cacheKey) && 
      permissionCacheTimestamps.has(cacheKey) && 
      (now - (permissionCacheTimestamps.get(cacheKey) || 0) < CACHE_EXPIRY)) {
    return permissionCache.get(cacheKey) || false;
  }
  
  const result = hasPermission(user.role as Role, requiredPermission);
  
  // Update cache and timestamp
  permissionCache.set(cacheKey, result);
  permissionCacheTimestamps.set(cacheKey, now);
  
  return result;
}

export function usePermission(requiredPermission: Permission): boolean {
  const user = useAuthStore(state => state.user);
  
  return useMemo(() => {
    if (!user || !user.role) return false;
    return cachedHasPermission(user, requiredPermission);
  }, [user, requiredPermission]);
}
